import React, { useState } from "react";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import type { PlanTier, BillingFrequency } from "../../api/billing";
import { createSubscription } from "../../api/billing";

export interface NapkinCheckoutFormProps {
  tier: PlanTier;
  frequency: BillingFrequency;
  onSuccess: (data: any) => void;
  onError: (err: any) => void;
}

const TIER_LABELS: Record<string, string> = {
  pro: "Pro",
  business: "Business",
};

const NapkinCheckoutForm: React.FC<NapkinCheckoutFormProps> = ({
  tier,
  frequency,
  onSuccess,
  onError,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError("");

    try {
      const card = elements.getElement(CardNumberElement);
      if (!card) throw new Error("Card element missing");

      const { error: pmErr, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card,
      });

      if (pmErr || !paymentMethod) throw new Error(pmErr?.message);

      const data = await createSubscription(tier, frequency, paymentMethod.id);

      // Handle 3D Secure / SCA if required
      if (data?.clientSecret && (data?.status === "requires_action" || data?.status === "incomplete")) {
        setError("");
        const { error: confirmErr, paymentIntent } = await stripe.confirmCardPayment(data.clientSecret);
        if (confirmErr) {
          const msg = confirmErr.message || "3D Secure authentication failed";
          setError(msg);
          onError(msg);
          return;
        }
        if (paymentIntent?.status === "succeeded") {
          onSuccess(data);
        } else {
          const msg = "Payment could not be confirmed. Please try again.";
          setError(msg);
          onError(msg);
        }
        return;
      }

      if (data?.success) {
        onSuccess(data);
      } else {
        const msg = data?.error || "Subscription failed";
        setError(msg);
        onError(msg);
      }
    } catch (err: any) {
      const msg = err?.message || "Unknown error";
      setError(msg);
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  const cardStyle = {
    base: {
      fontSize: "14px",
      color: "#0f172a",
      "::placeholder": { color: "#94a3b8" },
    },
    invalid: { color: "#be123c" },
  };

  const label = TIER_LABELS[tier] || tier;

  return (
    <form className="checkout-form" onSubmit={handleSubmit}>
      {/* CARD NUMBER */}
      <div className="form-row">
        <label>CARD NUMBER</label>
        <div className="card-element-container">
          <CardNumberElement options={{ style: cardStyle }} />
        </div>
      </div>

      {/* EXP + CVC */}
      <div className="form-row expiration-and-cvc">
        <div className="expiration">
          <label>EXPIRATION DATE</label>
          <div className="card-element-container">
            <CardExpiryElement options={{ style: cardStyle }} />
          </div>
        </div>
        <div className="cvc">
          <label>CVC</label>
          <div className="card-element-container">
            <CardCvcElement options={{ style: cardStyle }} />
          </div>
        </div>
      </div>

      {error && <div className="checkout-error">{error}</div>}

      <button
        className="primary-button checkout-submit"
        type="submit"
        disabled={loading || !stripe}
      >
        {loading ? "Processing..." : `Subscribe to ${label}`}
      </button>
    </form>
  );
};

export default NapkinCheckoutForm;
